import styled from "@emotion/styled";
import { parseJSON } from "date-fns";
import { Theme, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import { HistoricQuote } from "@api";
import {
  InvestmentQuoteNotationEnum,
  InvestmentTypeEnum,
  QuoteV2PriceUnitEnum,
  ShowHistoricQuoteIntervalEnum,
  useIntraday,
  useListAndSearchAllDividends,
  useShowHistoricQuote,
} from "@rentablo/api/apiv1";
import {
  addAreaValues,
  getInterval,
  getValueByInvestmentAndBookingType,
} from "@helpers";
import { GainLossPercents, MandatorTypography } from "@rentablo/components";
import { InvestmentChart, PHONE_CHART_HEIGHT } from "@components/Chart";
import {
  MobileRangeControl,
  useChartControls,
} from "@components/Chart/ChartControls";
import { useGetHistoricQuotesLookup } from "@api/v1/hooks/useLookupHistoricQuotes";
import { mergeComparisonQuotesByDate } from "@features/investments/investment/utils/mergeQuotes";
import { mergeCloseByDate } from "@features/investments/investment/utils/mergeCloseByDate";
import { QUOTE_PROVIDERS, STOCK_INDEXES } from "@constants";

const InvestmentChartStyled = styled(InvestmentChart)`
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  ${({ theme }) => theme.breakpoints.down("sm")} {
    box-shadow: none;
  }
`;

const QuoteChartTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    margin: ${({ theme }) => theme.spacing(0, -2)};
  }
`;

const SecurityChartTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
`;

interface QuoteChartContainerProps {
  quoteTickerSymbol: string;
  quoteProvider: string;
  quoteType: InvestmentTypeEnum;
  quoteCurrentValue: number;
  quoteChangePercentAbs: number;
  quoteIsin: string;
  quoteCurrency: string;
  quoteNotation?: InvestmentQuoteNotationEnum | QuoteV2PriceUnitEnum;
}

export const QuoteChartContainer = (props: QuoteChartContainerProps) => {
  const {
    quoteTickerSymbol,
    quoteProvider,
    quoteType,
    quoteCurrentValue,
    quoteChangePercentAbs,
    quoteIsin,
    quoteCurrency,
    quoteNotation,
  } = props;

  const { t } = useTranslation();
  const isSm = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const {
    rangeProps,
    comparedTicker,
    setComparedTicker,
    availableDateRanges,
    availableCompactDateRanges,
  } = useChartControls();

  const chartStartDate = rangeProps.range.startDate;
  const chartEndDate = rangeProps.range.endDate;
  const shouldRequestIntradayQuotes = rangeProps.intervalType === "oneDay";
  const interval = getInterval(chartStartDate, chartEndDate);

  const { data: securityHistoricQuotesLookup, isPending: isPendingHQLookup } =
    useGetHistoricQuotesLookup(
      {
        tickerSymbol: quoteTickerSymbol,
        quoteProvider: quoteProvider,
        from: chartStartDate.toISOString(),
        to: chartEndDate.toISOString(),
        interval,
      },
      {
        enabled:
          Boolean(quoteTickerSymbol && quoteProvider) &&
          !shouldRequestIntradayQuotes,
      }
    );

  const { data: comparedHistoricQuotesLookup } = useShowHistoricQuote(
    {
      quoteProvider: QUOTE_PROVIDERS.ariva,
      tickerSymbol: comparedTicker ?? "",
      from: chartStartDate.toISOString(),
      to: chartEndDate.toISOString(),
      interval: interval as ShowHistoricQuoteIntervalEnum, //TODO: Remove once swagger is updated to show day interval,
    },
    {
      enabled: Boolean(comparedTicker) && !shouldRequestIntradayQuotes,
    }
  );

  const { data: securityDividends } = useListAndSearchAllDividends(
    {
      isin: [quoteIsin],
      excludeImported: false,
      taxRate: 0,
    },
    {
      enabled: Boolean(quoteIsin),
    }
  );

  const isIntradayEnabled =
    !!quoteProvider && !!quoteTickerSymbol && shouldRequestIntradayQuotes;

  const { data: intraday, isPending: isPendingIntradayState } = useIntraday(
    {
      tickerSymbol: quoteTickerSymbol,
      quoteProvider: quoteProvider,
      period: "intraday10",
      from: chartStartDate.toISOString(),
      to: chartEndDate.toISOString(),
    },
    {
      enabled: isIntradayEnabled,
    }
  );

  const isLoadingIntraday = isIntradayEnabled ? isPendingIntradayState : false;

  const { data: comparisonIntraday } = useIntraday(
    {
      tickerSymbol: comparedTicker ?? "",
      quoteProvider: QUOTE_PROVIDERS.ariva,
      period: "intraday10",
      from: chartStartDate.toISOString(),
      to: chartEndDate.toISOString(),
    },
    {
      enabled: Boolean(comparedTicker) && shouldRequestIntradayQuotes,
    }
  );

  const chartHeaderValue =
    getValueByInvestmentAndBookingType({
      value: quoteCurrentValue,
      investmentType: quoteType,
      currency: quoteCurrency,
      percentageRange: "0-100",
      quoteNotation,
    }) ?? 0;

  const dividends =
    securityDividends?.dividends?.map(({ date, dividendPerShare }) => ({
      date: parseJSON(date as string),
      dividendPerShare: dividendPerShare ?? 0,
    })) || [];

  const securityHistoricQuotes =
    securityHistoricQuotesLookup?.historicQuotes?.[quoteTickerSymbol] ?? [];
  const comparedHistoricQuotes =
    (comparedHistoricQuotesLookup?.historicQuotes ?? []) as HistoricQuote[];

  const preparedIntradayData: HistoricQuote[] =
    intraday?.quoteBars?.map(({ dateTime, close }) => ({
      date: dateTime!,
      value: close!,
    })) ?? [];

  const preparedComparisonIntraday: HistoricQuote[] =
    comparisonIntraday?.quoteBars?.map(({ dateTime, close }) => ({
      date: dateTime!,
      value: close!,
    })) ?? [];

  // Note: we need to reverse the data, because backend sends quotes in DESC order
  const mainQuotes = shouldRequestIntradayQuotes
    ? preparedIntradayData.slice().reverse()
    : securityHistoricQuotes;
  const comparisonQuotes = shouldRequestIntradayQuotes
    ? preparedComparisonIntraday.slice().reverse()
    : comparedHistoricQuotes;

  const preparedHistoricQuotes = comparedTicker
    ? mergeComparisonQuotesByDate(mainQuotes, comparisonQuotes)
    : mainQuotes;

  const chartDataWithAreaValues = addAreaValues(
    preparedHistoricQuotes.map(({ date, ...rest }) => ({
      ...rest,
      date: parseJSON(date),
    })),
    "value"
  );

  const chartData = mergeCloseByDate(
    chartDataWithAreaValues,
    dividends || [],
    []
  );

  return (
    <InvestmentChartStyled
      showEvents
      drawLines={!isSm}
      showNavigator={!isSm}
      chartHeight={isSm ? PHONE_CHART_HEIGHT : undefined}
      hideRangeSelector={isSm}
      figureAction={isSm ? <MobileRangeControl /> : undefined}
      rangeProps={rangeProps}
      isFetching={
        shouldRequestIntradayQuotes ? isLoadingIntraday : isPendingHQLookup
      }
      comparedTicker={comparedTicker}
      comparisonValues={STOCK_INDEXES}
      data={chartData}
      availableDateRanges={availableDateRanges}
      availableCompactDateRanges={availableCompactDateRanges}
      title={
        <QuoteChartTitleWrapper>
          <Typography variant="body2">
            {t("components.quotes.quoteChartContainer.subheader")}
          </Typography>
          <SecurityChartTitle>
            <MandatorTypography
              variant={isSm ? "h4" : "h2"}
              kpmg-germany={{ variant: isSm ? "h5" : "h3" }}
            >
              {chartHeaderValue}
            </MandatorTypography>
            <GainLossPercents value={(quoteChangePercentAbs ?? 0) / 100} />
          </SecurityChartTitle>
        </QuoteChartTitleWrapper>
      }
      onComparedTickerChange={(value) => setComparedTicker(value ?? undefined)}
    />
  );
};
